/**
* @copyright Copyright (C) 2020 Kokoon - All Rights Reserved
* Unauthorized copying of this file, via any medium is strictly prohibited
* Proprietary and confidential
*/

import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import { useTranslation } from 'react-i18next';
import { KnActionLink } from 'components/Link';
import KnEditIcon from 'components/icons/EditIcon';
import KnDeleteIcon from 'components/icons/DeleteIcon';
import KnDuplicateIcon from 'components/icons/DuplicateIcon';
import KnIconButton from 'components/IconButton';
import { TITRATIONS_CONTEXT, REGIMEN_STATUS } from 'Constants';
import { composeTitrationMedicationLabel } from 'utils/utils';
import {
  KnTitrationNameContainer,
  KnTitrationMedsContainer,
  KnTitrationActionsContainer,
  KnTitrationSubtleText,
  KnTitrationMedsText,
} from '../styles';

const KnTitrationItemInfo = (props) => {
  const { titration, context, actions } = props;
  const { t: translate } = useTranslation();

  const onAction = useCallback((e, actionCallback, titrationData) => {
    e.stopPropagation();
    if (actionCallback) {
      actionCallback(titrationData);
    }
  }, []);

  const titrationName = useMemo(() => {
    switch (context) {
      case TITRATIONS_CONTEXT.assignedRegimen:
        return translate('PATIENT_RECORD.medicationRegimen.assignedRegimenTitle');
      case TITRATIONS_CONTEXT.pastRegimen:
        if (titration.startedAt) {
          return translate(
            'PATIENT_RECORD.medicationRegimen.pastRegimenTitle',
            {
              startDate: titration.startedAt,
              endDate: titration.deactivatedAt || titration.completedAt,
            },
          );
        }
        return translate('PATIENT_RECORD.medicationRegimen.neverStartedRegimenTitle');
      default:
        return titration.name;
    }
  }, [titration, context, translate]);

  const titrationDate = useMemo(() => {
    switch (context) {
      case TITRATIONS_CONTEXT.assignedRegimen:
      case TITRATIONS_CONTEXT.pastRegimen:
        if (!titration.startedAt) {
          return translate(
            'PATIENT_RECORD.medicationRegimen.assignedAt',
            { date: titration.assignedAt },
          );
        }
        if (titration.status === REGIMEN_STATUS.active) {
          return translate(
            'PATIENT_RECORD.medicationRegimen.startedAt',
            { date: titration.startedAt },
          );
        }
        /** When started and deactivated/completed, show no date, as title holds both dates */
        return '';
      default:
        return translate('TITRATIONS.lastUpdated', { date: titration.lastUpdated });
    }
  }, [titration, context, translate]);

  return (
    <>
      <Box display="flex" alignItems="baseline" flex={1}>
        <KnTitrationNameContainer>
          <Typography variant="h6" color="primary">{titrationName}</Typography>
        </KnTitrationNameContainer>
        <KnTitrationMedsContainer>
          {titration.medications.map((presetMedication) => (
            <Typography
              key={presetMedication.index}
              variant="body1"
              component={KnTitrationMedsText}
            >
              {composeTitrationMedicationLabel(presetMedication)}
            </Typography>
          ))}
        </KnTitrationMedsContainer>
        {context === TITRATIONS_CONTEXT.titrationsManagement
          && (
            <KnTitrationActionsContainer>
              <KnActionLink
                data-testid="edit-titration-button"
                LhsIcon={KnEditIcon}
                onClick={(e) => onAction(e, actions.onEditTitration, titration)}
              >
                {translate('GENERAL.editButton')}
              </KnActionLink>
              <KnActionLink
                data-testid="duplicate-titration-button"
                LhsIcon={KnDuplicateIcon}
                onClick={(e) => onAction(e, actions.onDuplicateTitration, titration)}
              >
                {translate('TITRATIONS.duplicateButton')}
              </KnActionLink>

              <KnIconButton
                data-testid="delete-titration-button"
                Icon={KnDeleteIcon}
                onClick={(e) => onAction(e, actions.onDeleteTitration, titration)}
              />
            </KnTitrationActionsContainer>
          )}

        {(context === TITRATIONS_CONTEXT.assignPreset) && (
          <KnTitrationActionsContainer>
            <KnActionLink
              data-testid="edit-preset-before-assigning-button"
              LhsIcon={KnEditIcon}
              onClick={(e) => onAction(e, actions.onEditTitration, titration)}
            >
              {translate('ASSIGN_REGIMEN.editBeforeAssigning')}
            </KnActionLink>
          </KnTitrationActionsContainer>
        )}

        {context === TITRATIONS_CONTEXT.assignedRegimen
          && titration.status === REGIMEN_STATUS.notStarted
          && (
            <KnTitrationActionsContainer>
              {actions.onEditMedicationRegimen
                && (
                  <KnActionLink
                    data-testid="edit-medication-regimen-button"
                    LhsIcon={KnEditIcon}
                    onClick={(e) => onAction(e, actions.onEditMedicationRegimen, titration)}
                  >
                    {translate('GENERAL.editButton')}
                  </KnActionLink>
                )}
              {actions.onDeactivateMedicationRegimen
                && (
                  <KnIconButton
                    data-testid="deactivate-medication-regimen-button"
                    Icon={KnDeleteIcon}
                    onClick={(e) => onAction(e, actions.onDeactivateMedicationRegimen, titration)}
                  />
                )}
            </KnTitrationActionsContainer>
          )}

      </Box>
      <Box>
        <Typography variant="body2" component={KnTitrationSubtleText}>
          {titrationDate}
        </Typography>
      </Box>
    </>
  );
};

KnTitrationItemInfo.propTypes = {
  titration: PropTypes.shape().isRequired,
  context: PropTypes.oneOf(
    [TITRATIONS_CONTEXT.assignPreset, TITRATIONS_CONTEXT.titrationsManagement,
      TITRATIONS_CONTEXT.assignedRegimen, TITRATIONS_CONTEXT.pastRegimen],
  ).isRequired,
  actions: PropTypes.shape(),
};

KnTitrationItemInfo.defaultProps = {
  actions: {},
};

export default KnTitrationItemInfo;
